import {
  ExtranetServiceDescriptionRouteDto,
  MultilingualKeyDto,
} from '@pharmac-extranet/extranet-api'
import { useLanguage } from '@pharmac-extranet/shared'
import { PharmacIconProps } from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { ExtranetRouteObject, extranetRoutes } from '../../router/ExtranetRouter'
import { checkAuthorizationFlags, useAuthorizationFlags } from '../../router/RouteGuards'
import * as ExtranetRouterUtils from '../../router/router-utils'
import { api } from '../../services/HttpService'

type NavigationLinkMeta = {
  path: MultilingualKeyDto
}

export type NavigationLink = {
  label: string
  path?: string
  icon?: PharmacIconProps['icon']
  meta?: NavigationLinkMeta
  children?: NavigationLink[]
}

export const useExtranetLinks = (): Array<NavigationLink> => {
  const { key, i18n, t } = useLanguage()

  const { customer } = useCustomerContext()
  const userFlags = useAuthorizationFlags()

  const { language } = i18n

  const filterInvalidLinks = (route: ExtranetRouteObject): boolean => {
    if (route.onlyRoute) return false

    const authorizationResult = checkAuthorizationFlags(route.protection, userFlags)

    return authorizationResult.includes('SUCCESS')
  }

  const routes = [...extranetRoutes()].filter(filterInvalidLinks)

  const routesQuery = useQuery({
    queryKey: ['__dynamicRoutes', { customerId: customer?.id }],
    enabled: typeof customer?.id === 'number',
    queryFn: async () =>
      await api.services.getServicesRoutes((customer?.id ?? '') as unknown as number),
    placeholderData: [],
  })

  const routeToLink = (a: ExtranetRouteObject): NavigationLink => {
    const path = ExtranetRouterUtils.localizeLinkPath(language, a.path) as string

    const metaFromRoute: NavigationLinkMeta = {
      path: {
        fi: ExtranetRouterUtils.getRoutePathForLanguage(a.path as string, 'fi'),
        en: ExtranetRouterUtils.getRoutePathForLanguage(a.path as string, 'en'),
        sv: ExtranetRouterUtils.getRoutePathForLanguage(a.path as string, 'sv'),
      },
    }

    return {
      icon: a.icon ?? undefined,
      path,
      label: t(a.name as unknown as TemplateStringsArray),
      children: (a.children || []).filter(filterInvalidLinks).map(routeToLink),
      meta: metaFromRoute,
    }
  }

  const dynamicServiceRouteToLink = (a: ExtranetServiceDescriptionRouteDto): NavigationLink => {
    const prefix = language !== 'fi' ? `/${language}` : ''

    const path =
      prefix + ExtranetRouterUtils.getRoutePathForLanguage('services', language) + '/' + key(a.path)

    return {
      path,
      label: !!key(a.menuLabel) ? key(a.menuLabel) : key(a.label),
      children: (a.children || []).map(dynamicServiceRouteToLink),
      meta: {
        path: a.path,
      },
    }
  }

  return useMemo(() => {
    if (!routesQuery.data || routesQuery.isLoading) return routes.map(routeToLink)
    const rootRoute = routes.find((a) => a.name === 'Root')

    if (!rootRoute || !rootRoute.children) {
      throw new Error('Routes are misconfigured')
    }

    const wildCardRemoved = rootRoute.children.filter((a) => a.path !== 'wildcard')
    const unprivilegedRemoved = wildCardRemoved.filter(filterInvalidLinks)

    return unprivilegedRemoved.map((curr) => {
      if (curr.index && curr.name === 'menu.home') {
        return {
          path: language === 'fi' ? '/' : '/' + language,
          label: t(curr.name),
          icon: 'home',
        }
      }

      if (curr.name === 'menu.services') {
        const link = routeToLink(curr)

        link.children = routesQuery.data.map(dynamicServiceRouteToLink)

        return link
      }

      return routeToLink(curr)
    })
  }, [language, routesQuery.data, routesQuery.isLoading, userFlags, customer])
}
